import { useEffect } from "react"
import { useReloadKey } from "../hooks/useReloadKey"
import produce, { setAutoFreeze } from "immer"

setAutoFreeze(false)

type IStoreAction = { type: string }

export interface IStore<State, Actions extends IStoreAction> {
  dispatch(action: Actions): void
  getState(): State
  addStateListener(callback: (newState: State, oldState: State) => void): () => void
  addActionListener(
    callback: (action: Actions, oldState: State, newState: State) => void,
  ): () => void
}

type ReducerFunction<State, Actions> = (state: State, action: Actions) => void | State
type ReducerObject<State, Actions extends { type: string }> = {
  [key in Actions["type"]]?: (state: State, payload: Actions & { type: key }) => any
}

export function createStoreBuilder<State, Actions extends { type: string }>(
  reducer: ReducerFunction<State, Actions> | ReducerObject<State, Actions>,
) {
  return (initialState: State): IStore<State, Actions> => {
    const reducerFunction = typeof reducer === "function" ? reducer : actionObject(reducer)
    const wrappedReducer: any = produce(reducerFunction as any)

    const actionBuffer: Actions[] = []
    let bufferProcessing = false

    const stateRef = { current: initialState }
    let stateListeners: ((newState: State, oldState: State) => void)[] = []
    let actionListeners: ((action: Actions, newState: State, oldState: State) => void)[] = []

    const getState = () => stateRef.current

    const dispatch = (action: Actions) => {
      actionBuffer.push(action)
      processBuffer()
    }

    function triggerStateListeners(newState: State, oldState: State) {
      stateListeners.forEach(listener => {
        try {
          listener(newState, oldState)
        } catch (err) {
          console.error("Error in state listener", listener, err)
        }
      })
    }
    function triggerActionListeners(action: Actions, newState: State, oldState: State) {
      actionListeners.forEach(listener => {
        try {
          listener(action, newState, oldState)
        } catch (err) {
          console.error("Error in action listener", listener, err)
        }
      })
    }

    function processBuffer() {
      // if something is already processing then exit
      if (bufferProcessing) {
        return
      }

      bufferProcessing = true

      const action = actionBuffer.shift()

      if (!action) {
        bufferProcessing = false
        return
      }

      const newState = wrappedReducer(stateRef.current as any, action) as State

      if (newState !== stateRef.current) {
        updateState(newState)
      }

      triggerActionListeners(action, stateRef.current, newState)

      bufferProcessing = false

      processBuffer()
    }

    const updateState = (newState: State) => {
      const oldState = stateRef.current
      stateRef.current = newState

      triggerStateListeners(newState, oldState)
    }

    const addStateListener = (listener: (typeof stateListeners)[0]) => {
      stateListeners.push(listener)

      return () => {
        stateListeners = stateListeners.filter(cb => cb !== listener)
      }
    }
    const addActionListener = (listener: (typeof actionListeners)[0]) => {
      actionListeners.push(listener)

      return () => {
        actionListeners = actionListeners.filter(cb => cb !== listener)
      }
    }

    return {
      dispatch,
      getState,
      addStateListener,
      addActionListener,
    }
  }
}

export function useStoreState<State, Actions extends IStoreAction>(store: IStore<State, Actions>) {
  const [_, triggerReloader] = useReloadKey()

  useEffect(() => {
    const cleanup = store.addStateListener(() => {
      triggerReloader()
    })
    return () => cleanup()
  }, [])

  return store.getState()
}

function actionObject<State, Actions extends IStoreAction>(
  handlers: ReducerObject<State, Actions>,
) {
  return (state: State, action: Actions) => {
    const handler = handlers[action.type as Actions["type"]]
    if (handler) {
      return handler(state, action)
    }
  }
}
